/**
 * Bodies: what a creature leaves where it fell, at each of the three stages
 * it goes through before it is gone. Asked for on 2026-09-25: "implement
 * corpses with the large decay time ... note that the dwarf king and queen
 * should use the same graphics, the queen's body still has a beard", and
 * "dead players should also leave a body, color matching the player".
 *
 * The server says which stage by the item it sends (a fresh body, a rotting
 * one, remains) and whose by the kind — a monster's key — or, for a player,
 * by their colour and race; see internal/game/corpse.go. Everything else is
 * here: the drawings were the proposal's, drawn in the game's own units, 32
 * to a tile, with the origin at the top-left of the tile the body lies on.
 * Big bodies spill onto the tiles round them the way their living sprites
 * do, so a body sprite is three tiles square with its own tile in the
 * middle, and the renderer centres it on that tile like any ground sprite
 * bigger than a tile.
 */

import { raceLook, type RaceLook } from "./sprites";
import { keptContext } from "./paper";

type Ctx = CanvasRenderingContext2D;

/** The body items' ids, which are the wire's: see ItemCorpse in items.go. */
export const BODY_FRESH = 57;
export const BODY_ROTTING = 58;
export const BODY_REMAINS = 59;

export function isBody(id: number): boolean {
  return id === BODY_FRESH || id === BODY_ROTTING || id === BODY_REMAINS;
}

/**
 * What lies on the ground in the order it is drawn: every body first, then
 * everything else, each in the order it came. Asked for on 2026-09-27:
 * "corpses should always appear behind items on the ground". A body is laid
 * where something fell, and loot and whatever was already lying there has
 * to show on top of it, however the stack came to be in the order it is —
 * and a body is drawn bigger than its tile, so it has to go down before the
 * things on the tiles round it as well as on its own, which is why this is
 * the whole list and not each tile's.
 */
export function bodiesFirst<T extends { id: number }>(items: readonly T[]): T[] {
  const bodies: T[] = [];
  const rest: T[] = [];
  for (const it of items) (isBody(it.id) ? bodies : rest).push(it);
  return bodies.concat(rest);
}

const BONE = "#ddd3bb", BONE_D = "#a89c80", ROT = "#6d6a45";

function hex(h: string): number[] {
  return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
}
function toHex(r: number[]): string {
  return "#" + r.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");
}
function mix(a: string, b: string, t: number): string {
  const x = hex(a), y = hex(b);
  return toHex(x.map((v, i) => v + (y[i] - v) * t));
}
/** What a colour has become by this stage: rotting pulls it toward a sick olive and down. */
function aged(c: string, st: number): string {
  return st === 1 ? mix(mix(c, ROT, 0.42), "#000000", 0.18) : c;
}

function ell(ctx: Ctx, cx: number, cy: number, rx: number, ry: number, c: string, rot = 0) {
  ctx.fillStyle = c;
  ctx.beginPath();
  ctx.ellipse(cx, cy, Math.max(rx, 0.1), Math.max(ry, 0.1), rot, 0, Math.PI * 2);
  ctx.fill();
}
function rect(ctx: Ctx, x: number, y: number, w: number, h: number, c: string) {
  ctx.fillStyle = c;
  ctx.fillRect(x, y, w, h);
}
function line(ctx: Ctx, pts: [number, number][], c: string, w: number) {
  ctx.strokeStyle = c;
  ctx.lineWidth = w;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.stroke();
}
function poly(ctx: Ctx, pts: [number, number][], c: string) {
  ctx.fillStyle = c;
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.closePath();
  ctx.fill();
}
function flies(ctx: Ctx, x: number, y: number, n = 3) {
  const at: [number, number][] = [[0, 0], [5, -3], [-4, -5], [8, 2], [2, -8]];
  for (let i = 0; i < n; i++) {
    rect(ctx, x + at[i][0], y + at[i][1], 1, 1, "#141414");
    rect(ctx, x + at[i][0] - 1, y + at[i][1] - 1, 1, 1, "rgba(220,230,240,.7)");
  }
}
function shadow(ctx: Ctx, cx: number, cy: number, rx: number, ry: number) {
  ell(ctx, cx, cy, rx, ry, "rgba(0,0,0,.22)");
}
function closedEye(ctx: Ctx, x: number, y: number) {
  rect(ctx, x, y, 2, 1, "#1b1410");
}
function xEye(ctx: Ctx, x: number, y: number) {
  rect(ctx, x, y, 1, 1, "#1b1410");
  rect(ctx, x + 1, y + 1, 1, 1, "#1b1410");
  rect(ctx, x + 1, y - 1, 1, 1, "#1b1410");
  rect(ctx, x, y + 2, 1, 1, "#1b1410");
}
function ribs(ctx: Ctx, x0: number, x1: number, cy: number, h: number, c: string) {
  for (let x = x0; x <= x1; x += 2.2) line(ctx, [[x, cy - h], [x + 0.8, cy], [x, cy + h]], c, 1);
}
function skull(ctx: Ctx, x: number, y: number, r: number, c = BONE) {
  ell(ctx, x, y, r, r * 0.85, c);
  rect(ctx, x - r * 0.45, y - 0.5, 1.4, 1.4, "#3a3228");
  rect(ctx, x + r * 0.15, y - 0.5, 1.4, 1.4, "#3a3228");
}

interface Quad {
  body: string; back: string; belly: string; ear?: string; tail?: "thin" | "bushy" | "stub"; tailc?: string;
  size: number; snout?: number; fur?: string; stripe?: string;
}

/** A four-legged beast on its side, head to the right, legs stiff toward the viewer. */
function quad(ctx: Ctx, o: Quad, st: number) {
  const s = o.size;
  ctx.save();
  ctx.translate(16, 26); ctx.scale(s, s); ctx.translate(-16, -26);
  const snout = o.snout ?? 2.6;
  if (st < 2) {
    const body = aged(o.body, st), back = aged(o.back, st), belly = aged(o.belly, st);
    const sag = st === 1 ? 0.82 : 1;
    shadow(ctx, 16, 24, 14, 3.5);
    for (const [x, d] of [[9, -2], [12, 1], [20, -1], [23, 2]]) line(ctx, [[x, 23], [x + d, 29]], back, 2);
    if (o.tail === "thin") line(ctx, [[6, 20], [2, 22], [-1, 21]], aged(o.tailc ?? o.back, st), 1);
    if (o.tail === "bushy") ell(ctx, 3.5, 21, 3.8, 2.2, back, 0.3);
    ell(ctx, 16, 20.5, 11, 5.5 * sag, body);
    ell(ctx, 16, 18.5 + (1 - sag) * 3, 10, 2.6 * sag, back);
    ell(ctx, 16, 23, 7.5, 2 * sag, belly);
    ell(ctx, 27, 19.5, 4.2, 3.4 * sag, body);
    ell(ctx, 30.5, 21, snout, 1.8, body);
    rect(ctx, 32.2 + snout - 3, 20.5, 1.4, 1.2, "#2a1f18");
    poly(ctx, [[25, 17], [26.5, 13.5], [28.5, 16.5]], aged(o.ear ?? o.back, st));
    xEye(ctx, 28, 18);
    if (o.stripe) rect(ctx, 11, 17, 10, 1, aged(o.stripe, st));
    if (st === 1) flies(ctx, 17, 12);
  } else {
    shadow(ctx, 16, 24, 12, 2.5);
    if (o.fur) ell(ctx, 16, 21, 9, 3, o.fur);
    line(ctx, [[5, 20], [24, 19]], BONE, 1.4);
    ribs(ctx, 10, 19, 20, 3, BONE);
    for (const [x, d] of [[9, -2], [12, 1], [20, -1], [23, 2]]) line(ctx, [[x, 22], [x + d, 28]], BONE_D, 1);
    skull(ctx, 27, 19.5, 3.2);
    rect(ctx, 29.5, 20, snout + 1, 1.6, BONE);
    if (o.tail === "thin") line(ctx, [[5, 20], [1, 22]], BONE_D, 0.8);
  }
  ctx.restore();
}

interface Biped {
  skin: string; torso: string; legs: string; boot?: string; arm?: string; belt?: string; paint?: string;
  spots?: string; mail?: boolean; cloth?: string; size: number;
  head?: (ctx: Ctx, st: number, skin: string) => void;
  keeps?: (ctx: Ctx) => void;
}

/** Somebody on their back, head to the left, arms flung out. */
function biped(ctx: Ctx, o: Biped, st: number) {
  const s = o.size;
  ctx.save();
  ctx.translate(16, 20); ctx.scale(s, s); ctx.translate(-16, -20);
  const skin = aged(o.skin, st), torso = aged(o.torso, st), legs = aged(o.legs, st), boot = aged(o.boot ?? "#2a2118", st);
  const arm = o.arm ? aged(o.arm, st) : skin;
  if (st < 2) {
    shadow(ctx, 16, 22, 15, 5);
    line(ctx, [[12, 15], [9, 9], [6, 7]], arm, 2.6);
    line(ctx, [[12, 25], [15, 30], [18, 31]], arm, 2.6);
    rect(ctx, 20, 16, 9, 3.4, legs); rect(ctx, 20, 20.6, 9, 3.4, legs);
    rect(ctx, 28, 15.6, 2.6, 4, boot); rect(ctx, 28, 20.4, 2.6, 4, boot);
    rect(ctx, 10, 14.5, 11, 11, torso);
    if (o.paint) { rect(ctx, 12, 14.5, 1.5, 11, aged(o.paint, st)); rect(ctx, 15.5, 14.5, 1.5, 11, aged(o.paint, st)); }
    if (o.belt) rect(ctx, 19, 14.5, 1.6, 11, aged(o.belt, st));
    if (o.mail) for (let y = 15.5; y < 25; y += 2) for (let x = 11 + (y % 4 ? 1 : 0); x < 20; x += 2) rect(ctx, x, y, 1, 1, aged("#5d6166", st));
    if (o.spots) for (const [x, y] of [[12, 17], [15, 22], [17, 16], [13, 23], [18, 20]]) rect(ctx, x, y, 1.6, 1.6, aged(o.spots, st));
    ell(ctx, 6.5, 20, 4.2, 4.2, skin);
    o.head?.(ctx, st, skin);
    if (st === 1) flies(ctx, 15, 10);
  } else {
    shadow(ctx, 16, 22, 13, 4);
    if (o.cloth) rect(ctx, 10, 15, 11, 10, o.cloth);
    line(ctx, [[10, 20], [20, 20]], BONE, 1.4);
    ribs(ctx, 11, 18, 20, 4, BONE);
    line(ctx, [[12, 15], [9, 9], [6, 7]], BONE_D, 1.1); line(ctx, [[12, 25], [15, 30], [18, 31]], BONE_D, 1.1);
    line(ctx, [[20, 18], [29, 17.5]], BONE, 1.2); line(ctx, [[20, 22], [29, 22.5]], BONE, 1.2);
    skull(ctx, 6.5, 20, 3.6);
    o.keeps?.(ctx);
  }
  ctx.restore();
}

// Heads, and what outlasts the flesh.
function orcHelm(ctx: Ctx) { poly(ctx, [[2, 16], [2, 24], [7, 24], [7, 16]], "#8d949b"); poly(ctx, [[2, 18], [-2, 20], [2, 22]], "#aab1b8"); rect(ctx, 6, 16, 1, 8, "#6e757c"); }
function dwarfHelm(ctx: Ctx) { poly(ctx, [[2.5, 16.5], [2.5, 23.5], [6, 23.5], [6, 16.5]], "#7c8288"); rect(ctx, 5.5, 18, 1.5, 4, "#2b2b2b"); }
// The dwarf soldier's cap (2026-09-27): the axeman's helm in a darker iron,
// with a rim, which is what lies there when the rest has gone.
function ironCap(ctx: Ctx) { poly(ctx, [[2.5, 16.5], [2.5, 23.5], [6, 23.5], [6, 16.5]], "#3e4247"); rect(ctx, 5.5, 16.5, 1.2, 7, "#23262a"); }
function goldHelm(ctx: Ctx) { rect(ctx, 2.2, 16.2, 4, 7.6, "#d8b24a"); rect(ctx, 5.8, 18, 1.2, 4, "#2b1d10"); poly(ctx, [[2.5, 18.5], [-2.5, 17], [-3, 22], [2.5, 21.5]], "#c02020"); }
function crown(ctx: Ctx) { rect(ctx, 2, 16.5, 2.4, 7, "#e0b83a"); for (let y = 16.5; y < 23.5; y += 2.3) rect(ctx, 0.8, y, 1.4, 1.2, "#e0b83a"); }

type Kind = { quad: Quad } | { biped: Biped } | { draw: (ctx: Ctx, st: number) => void };

const TROLL: Biped = {
  skin: "#74a64a", torso: "#6a9a44", legs: "#4d6b35", belt: "#5a3a1e", size: 1.4,
  head: (ctx) => { xEye(ctx, 7.5, 18); rect(ctx, 9.4, 17.2, 1.4, 1.2, "#efe6c8"); rect(ctx, 9.4, 21.6, 1.4, 1.2, "#efe6c8"); },
};

/** The hill troll's body: the troll's, grey and bigger (2026-09-28). */
const HILL_TROLL: Biped = { ...TROLL, skin: "#8e887c", torso: "#847e72", legs: "#5e584e", belt: "#4a3a2a", size: 1.7 };

/** The cave troll's body: the troll's, slate grey and the biggest of the three (2026-09-28). */
const CAVE_TROLL: Biped = { ...TROLL, skin: "#6b7466", torso: "#5f685a", legs: "#454c42", belt: "#35302a", size: 1.85 };

/** The stone troll's body: the troll's, in weathered brown grey, between the forest and the hill troll's size (2026-10-01). */
const STONE_TROLL: Biped = { ...TROLL, skin: "#8a7b66", torso: "#7e705c", legs: "#5e5243", belt: "#6a5a40", size: 1.55 };

// The Easterling's tall lacquered helm with its gold rim, and the Uruk-hai's
// black one with the White Hand on it (2026-10-01): what lies there when the
// rest has gone.
function easterlingHelm(ctx: Ctx) { poly(ctx, [[2.5, 16], [2.5, 24], [6, 24], [6, 16]], "#2a2220"); poly(ctx, [[2.5, 18.5], [-2.5, 20], [2.5, 21.5]], "#2a2220"); rect(ctx, 5.6, 16, 1, 8, "#d8b040"); }
function urukHelm(ctx: Ctx) { poly(ctx, [[2, 16], [2, 24], [7, 24], [7, 16]], "#34363b"); poly(ctx, [[2, 18], [-2, 20], [2, 22]], "#6c7078"); rect(ctx, 3.4, 19, 2, 2, "#f2f0e8"); }
// The Black Orc's helm (2026-10-04: "Add tougher Black Orcs in Mordor"):
// the Uruk-hai's shape in blacker iron, with the Red Eye where the White
// Hand is, a red lid round a black slit.
// Gundabad's helms (2026-10-15): the same shape in grey iron for its orcs,
// in black for Bolg's bodyguard, and Bolg's with a white tuft of wolf.
function gundabadHelm(ctx: Ctx, iron: string, light: string) { poly(ctx, [[1.6, 15.6], [1.6, 24.4], [7.2, 24.4], [7.2, 15.6]], iron); poly(ctx, [[1.6, 18], [-2.4, 20], [1.6, 22]], light); }
function bolgHelm(ctx: Ctx) { gundabadHelm(ctx, "#303034", "#6a6a74"); ell(ctx, 4.4, 20, 1.6, 2.6, "#e8e4d8"); }
function blackOrcHelm(ctx: Ctx) { poly(ctx, [[1.6, 15.6], [1.6, 24.4], [7.2, 24.4], [7.2, 15.6]], "#232328"); poly(ctx, [[1.6, 18], [-2.4, 20], [1.6, 22]], "#4c4c56"); ell(ctx, 4.4, 20, 1.2, 1.8, "#c81e0a"); rect(ctx, 4.1, 18.8, 0.6, 2.4, "#120404"); }
// Sauron's helm (2026-10-26: "Add ungoliant and Sauron as monsters but no
// specific spawn"): black iron over the whole head, a crown of five long
// spikes out past the top of it, and the fire still in the eye-slit.
function sauronHelm(ctx: Ctx) {
  poly(ctx, [[1.2, 15.4], [1.2, 24.6], [7.6, 24.6], [7.6, 15.4]], "#1a1a1e");
  for (const y of [15.8, 17.8, 20, 22.2, 24.2]) poly(ctx, [[1.4, y - 0.8], [-3.6 + Math.abs(y - 20) * 0.4, y], [1.4, y + 0.8]], "#2c2c34");
  rect(ctx, 4.6, 17.2, 0.8, 5.6, "#e0601a");
}
// The Wood-elves and their king (2026-10-07: "propose some pathways through
// the mirkwood forest, and the elf-kings halls, with hostile elf monsters",
// then "Implement it"): an elf's pointed ears out either side of the head,
// a hood, the guard's tall silver helm with its green crest, and the king's
// crown of leaves and berries, which withers brown with the rest of him.
function elfEars(ctx: Ctx, sk: string) { poly(ctx, [[5.4, 16.2], [3.8, 12.4], [7.6, 16.4]], sk); poly(ctx, [[5.4, 23.8], [3.8, 27.6], [7.6, 23.6]], sk); }
function elfHelm(ctx: Ctx) { poly(ctx, [[2.5, 16], [2.5, 24], [6, 24], [6, 16]], "#c8ced6"); poly(ctx, [[2.5, 17.5], [-3.5, 20], [2.5, 22.5]], "#aab2ba"); rect(ctx, 5.6, 16, 1, 8, "#2e6a34"); }
function leafCrown(ctx: Ctx, leaf: string, berry: string) {
  for (const y of [16.2, 18.1, 20, 21.9, 23.8]) ell(ctx, 2.6, y, 1.3, 0.9, leaf);
  for (const y of [17.2, 20.9]) rect(ctx, 1.6, y, 1, 1, berry);
}

const KINDS: Record<string, Kind> = {
  rat: { quad: { body: "#8a6a52", back: "#6f533f", belly: "#b0927a", ear: "#e3a3a3", tail: "thin", tailc: "#e3a3a3", size: 0.62, snout: 2.4 } },
  wolf: { quad: { body: "#8c8c8c", back: "#686868", belly: "#b7b7b7", tail: "bushy", size: 1.1, snout: 3.2 } },
  winterwolf: { quad: { body: "#e3eaef", back: "#bccad6", belly: "#ffffff", tail: "bushy", size: 1.15, snout: 3.2, fur: "#cfd9e1" } },
  bear: { quad: { body: "#5d3c22", back: "#4a2e18", belly: "#72502f", ear: "#3d2614", tail: "stub", size: 1.65, snout: 2.6, fur: "#4a2e18" } },
  // The bandit (2026-10-01): a man in a leather jerkin and a mottled green
  // cloak, the hood still up round his head and the scarf over his face, and
  // the cloak is what is left of him at the last.
  bandit: { biped: { skin: "#c9a07a", torso: "#6b4a2c", legs: "#3a3128", boot: "#231c15", arm: "#4a5236", belt: "#2a1f16", cloth: "#363c27", size: 1.15,
    head: (ctx, st) => {
      poly(ctx, [[8, 15.3], [3, 15], [0.2, 17], [-1.6, 20], [0.2, 23], [3, 25], [8, 24.7], [6, 22.5], [5.4, 20], [6, 17.5]], aged("#4a5236", st));
      rect(ctx, 7.6, 20.4, 2.6, 2.4, aged("#5a2a24", st));
      closedEye(ctx, 8, 18.4);
    } } },
  kobold: { biped: { skin: "#b87436", torso: "#8a5226", legs: "#7a4620", size: 0.9,
    head: (ctx, st, sk) => { poly(ctx, [[3, 16.5], [1, 13], [5, 16]], sk); poly(ctx, [[3, 23.5], [1, 27], [5, 24]], sk); ell(ctx, 2.2, 20, 2.4, 1.6, aged("#d9a068", st)); xEye(ctx, 6, 18.5); } } },
  // The hobbit (2026-09-28): the mustard waistcoat, hedge-coloured
  // breeches and bare brown feet, with its ginger curls round the head.
  hobbit: { biped: { skin: "#e0b48a", torso: "#c0922c", legs: "#5a5a30", boot: "#8a5a32", size: 0.85,
    head: (ctx, st) => { for (const [x, y] of [[4, 17], [3, 20], [4, 23], [6.5, 16], [6.5, 24]]) ell(ctx, x, y, 1.5, 1.5, aged("#9a4e1e", st)); closedEye(ctx, 7.5, 19); } } },
  // Bullroarer Took (2026-10-10): the hobbit a size up, in his Took green,
  // with brown curls. His saucepan and golf club lie beside him as loot.
  bullroarer: { biped: { skin: "#e0b48a", torso: "#2f6a3a", legs: "#5a4a32", boot: "#8a5a32", size: 1,
    head: (ctx, st) => { for (const [x, y] of [[4, 17], [3, 20], [4, 23], [6.5, 16], [6.5, 24]]) ell(ctx, x, y, 1.5, 1.5, aged("#5a3210", st)); closedEye(ctx, 7.5, 19); } } },
  // Gollum (2026-10-08: "add a gollum creature"): thin and pale, bare but
  // for a dark loincloth, a few lank hairs and the great pale eyes shut.
  gollum: { biped: { skin: "#9aa38c", torso: "#9aa38c", legs: "#7b846e", boot: "#9aa38c", belt: "#4d4636", size: 0.8,
    head: (ctx, st) => { for (const y of [18, 20, 22]) rect(ctx, 2.6, y, 1.6, 0.7, aged("#3a3a30", st)); closedEye(ctx, 7.5, 18.5); closedEye(ctx, 7.5, 21); } } },
  orc: { biped: { skin: "#5d8a3c", torso: "#8a8f94", mail: true, legs: "#3a3a3a", belt: "#4a3420", size: 1.2,
    head: (ctx) => { orcHelm(ctx); closedEye(ctx, 7.5, 18.5); rect(ctx, 9.5, 18.5, 1, 3, "#e8e0c8"); },
    keeps: (ctx) => { orcHelm(ctx); for (let y = 16; y < 25; y += 2) for (let x = 11; x < 20; x += 2) rect(ctx, x, y, 1, 1, "#7d8288"); } } },
  orcberserker: { biped: { skin: "#4f7d2c", torso: "#4f7d2c", paint: "#c0281c", legs: "#23231f", size: 1.3,
    head: (ctx, st) => { xEye(ctx, 7.5, 18); rect(ctx, 9.6, 18, 1, 3, "#e8e0c8"); rect(ctx, 3, 17, 3, 1, aged("#c0281c", st)); } } },
  gnoll: { biped: { skin: "#c8a068", torso: "#c8a068", spots: "#8a6030", legs: "#a8804c", belt: "#5a3a1e", size: 1.25,
    head: (ctx, st, sk) => { ell(ctx, 1.5, 20, 3, 1.8, aged("#6a4a2a", st)); poly(ctx, [[5, 16.5], [4, 12.5], [7.5, 16]], sk); poly(ctx, [[5, 23.5], [4, 27.5], [7.5, 24]], sk); xEye(ctx, 6.5, 18); } } },
  // A troll does not rot: it turns to stone, and the stone is what is left.
  troll: { draw: (ctx, st) => {
    if (st < 2) { biped(ctx, TROLL, st); return; }
    biped(ctx, { ...TROLL, skin: "#8b8d86", torso: "#7e807a", legs: "#6f716b", belt: "#6a6c66", boot: "#62645e", head: undefined }, 0);
    line(ctx, [[12, 16], [15, 21], [13, 25]], "#55574f", 0.8);
    line(ctx, [[22, 17], [25, 19]], "#55574f", 0.8);
  } },
  // The hill troll (2026-09-28): the troll's body a size up in the grey of
  // the pass, and it turns to stone the same way.
  hilltroll: { draw: (ctx, st) => {
    if (st < 2) { biped(ctx, HILL_TROLL, st); return; }
    biped(ctx, { ...HILL_TROLL, skin: "#8b8d86", torso: "#7e807a", legs: "#6f716b", belt: "#6a6c66", boot: "#62645e", head: undefined }, 0);
    line(ctx, [[11, 15], [15, 21], [12, 26]], "#55574f", 0.8);
    line(ctx, [[22, 16], [26, 19]], "#55574f", 0.8);
  } },
  // The troll chief of the Ettenmoors (2026-10-16, the Rangers' camp): the
  // hill troll's body bigger again and lichened, headless, since every kill
  // leaves the head; it turns to stone the same way.
  trollchief: { draw: (ctx, st) => {
    const chief: Biped = { ...HILL_TROLL, skin: "#868a7a", torso: "#7a7e6e", legs: "#585c4e", size: 1.95, head: undefined };
    if (st < 2) { biped(ctx, chief, st); return; }
    biped(ctx, { ...chief, skin: "#8b8d86", torso: "#7e807a", legs: "#6f716b", belt: "#6a6c66", boot: "#62645e" }, 0);
    line(ctx, [[10, 14], [15, 21], [11, 27]], "#55574f", 0.8);
    line(ctx, [[21, 15], [27, 19]], "#55574f", 0.8);
  } },
  // The cave troll (2026-09-28: "rename our current trolls as forest
  // trolls, add cave trolls and hill trolls"): the biggest troll's body in
  // the slate of the deep rock, its manacles still on, and it turns to
  // stone like the others.
  cavetroll: { draw: (ctx, st) => {
    if (st < 2) {
      biped(ctx, CAVE_TROLL, st);
      // The manacles, on the forearms where biped lays them, scaled as it is.
      ctx.save();
      ctx.translate(16, 20); ctx.scale(CAVE_TROLL.size, CAVE_TROLL.size); ctx.translate(-16, -20);
      rect(ctx, 6.9, 6.9, 1.6, 2.2, "#2a2c2e");
      rect(ctx, 15.8, 29.4, 1.6, 2.2, "#2a2c2e");
      ctx.restore();
      return;
    }
    biped(ctx, { ...CAVE_TROLL, skin: "#85877f", torso: "#787a73", legs: "#696b64", belt: "#62645e", boot: "#5c5e58", head: undefined }, 0);
    line(ctx, [[10, 14], [15, 21], [11, 27]], "#4e5049", 0.8);
    line(ctx, [[21, 15], [27, 19]], "#4e5049", 0.8);
  } },
  cyclops: { biped: { skin: "#c9956a", torso: "#c9956a", legs: "#c9956a", belt: "#5a3a1e", boot: "#5a3a1e", size: 1.8,
    head: (ctx) => { ell(ctx, 7, 20, 1.6, 1.2, "#f0e8e0"); rect(ctx, 6.2, 19.6, 1.8, 0.8, "#1b1410"); },
    keeps: (ctx) => { ell(ctx, 6.5, 20, 1.2, 1.2, "#3a3228"); } } },
  dwarf: { biped: { skin: "#e2b48c", torso: "#7a4e2a", legs: "#5a3a20", belt: "#3a2614", size: 1.0,
    head: (ctx, st) => { dwarfHelm(ctx); ell(ctx, 10.5, 20, 3.4, 3, aged("#8f8a82", st)); }, keeps: dwarfHelm } },
  // The soldier: the dwarf in a quilted coat of undyed wool and a dark iron
  // cap, bearded brown (2026-09-27: "add a dwarf soldier creature").
  dwarfsoldier: { biped: { skin: "#e2b48c", torso: "#9a8660", legs: "#5a4a30", belt: "#6f5f40", size: 1.0,
    head: (ctx, st) => { ironCap(ctx); ell(ctx, 10.5, 20, 3.4, 3, aged("#4a3222", st)); }, keeps: ironCap } },
  dwarfgeomancer: { biped: { skin: "#e2b48c", torso: "#5c2a24", legs: "#4a221c", size: 1.0,
    head: (ctx, st) => { rect(ctx, 2.5, 16, 1.6, 8, aged("#e87a2a", st)); ell(ctx, 10.5, 20, 3.4, 3, aged("#9a9a9a", st)); },
    keeps: (ctx) => { rect(ctx, 2.5, 16, 1.6, 8, "#8a5a3a"); ell(ctx, 14, 21, 2, 2, "#3a1a10"); } } },
  dwarfguard: { biped: { skin: "#e2b48c", torso: "#3d2d5e", legs: "#2e2248", belt: "#d8b24a", size: 1.05,
    head: (ctx, st) => { goldHelm(ctx); ell(ctx, 10.5, 20, 3.2, 2.8, aged("#8f6a44", st)); },
    keeps: (ctx) => { goldHelm(ctx); rect(ctx, 10, 15, 11, 10, "rgba(61,45,94,.55)"); } } },
  // The king, and the queen with him: she is drawn as he is, crown and
  // beard both (see BODY_AS).
  dwarfking: { biped: { skin: "#e2b48c", torso: "#4a2a6a", legs: "#3a2054", belt: "#d8b24a", size: 1.05,
    head: (ctx, st) => { crown(ctx); ell(ctx, 11, 20, 3.8, 3.2, aged("#eee6d2", st)); }, keeps: crown } },
  bat: { draw: bat },
  spider: { draw: spider },
  // The content proposal's kinds (2026-10-01: "Implement the entire
  // proposal in the background"), in the colours they were drawn alive in.
  // The Nazgûl is not here: it rises, and leaves bones rather than a body.
  // Nor is the Witch-king (2026-10-04, "Add more Nazgul and a boss Witch
  // King"), for the same reason.
  crebain: { draw: crebain },
  orcsnaga: { biped: { skin: "#6f8058", torso: "#5e4a32", legs: "#55633f", belt: "#8a7650", boot: "#3a2e22", size: 0.95,
    head: (ctx, st, sk) => { poly(ctx, [[4, 16.5], [1.5, 12], [5.5, 16]], sk); poly(ctx, [[4, 23.5], [1.5, 28], [5.5, 24]], sk); ell(ctx, 4, 20, 2.2, 3.2, aged("#1e1c18", st)); xEye(ctx, 7.5, 18.5); } } },
  warg: { quad: { body: "#4a3f35", back: "#251f1a", belly: "#7a6c5c", tail: "bushy", size: 1.35, snout: 3.2, fur: "#3a3129" } },
  // A werewolf summoned on its own (2026-11-01): the warg bigger and black. One Sauron calls leaves no body.
  werewolf: { quad: { body: "#26221f", back: "#0e0c0b", belly: "#4a433d", tail: "bushy", size: 1.55, snout: 3.4, fur: "#1a1715" } },
  southron: { biped: { skin: "#8a5a3a", torso: "#b0281e", legs: "#4a2a1c", boot: "#2a1a10", belt: "#e0b040", cloth: "#7a1a14", size: 1.15,
    head: (ctx, st) => { ell(ctx, 4, 20, 3, 4.6, aged("#b0281e", st)); rect(ctx, 5.4, 15.8, 1, 8.4, aged("#e0b040", st)); closedEye(ctx, 8, 18.4); },
    keeps: (ctx) => { rect(ctx, 3, 16, 3, 8, "#7a1a14"); } } },
  dunlending: { biped: { skin: "#d0a882", torso: "#6b5034", legs: "#4a3a2a", boot: "#3a2c1e", belt: "#2a1f16", cloth: "#4a3622", size: 1.15,
    head: (ctx, st) => { ell(ctx, 3.5, 20, 2.8, 5, aged("#3a2a1c", st)); poly(ctx, [[9, 17.5], [13, 19], [13, 21], [9, 22.5]], aged("#3a2a1c", st)); closedEye(ctx, 7.5, 18.4); } } },
  corsair: { biped: { skin: "#b98a62", torso: "#24324a", legs: "#3a3a40", boot: "#1c1612", belt: "#a82020", cloth: "#161f30", size: 1.15,
    head: (ctx, st) => { ell(ctx, 3.6, 20, 2.6, 4.4, aged("#9a1e1e", st)); rect(ctx, 1, 22, 2.4, 1.4, aged("#6a1414", st)); rect(ctx, 6.6, 23.6, 1, 1.2, "#f0c848"); closedEye(ctx, 8, 18.4); },
    keeps: (ctx) => { ell(ctx, 3.6, 20, 2.4, 4, "#6a1414"); } } },
  easterling: { biped: { skin: "#c9a070", torso: "#8a2a1e", legs: "#2e2a26", boot: "#1c1814", belt: "#2a1f16", mail: true, size: 1.15,
    head: (ctx) => { easterlingHelm(ctx); closedEye(ctx, 7.8, 18.4); }, keeps: easterlingHelm } },
  // 11. The Variags of Khand (2026-10-16): the axeman and the chieftain in
  // their scale under the Easterling's kind of helm, the horse-archer
  // bare-headed in his leather, black-haired.
  variagaxeman: { biped: { skin: "#7a5236", torso: "#3a2c26", legs: "#5a2a1c", boot: "#2a1a10", belt: "#b0281e", mail: true, size: 1.15,
    head: (ctx) => { easterlingHelm(ctx); closedEye(ctx, 7.8, 18.4); }, keeps: easterlingHelm } },
  variagarcher: { biped: { skin: "#7a5236", torso: "#8a6a3a", legs: "#4a2a1c", boot: "#2a1a10", belt: "#b0281e", size: 1.15,
    head: (ctx, st) => { ell(ctx, 3.5, 20, 2.8, 5, aged("#1a1410", st)); closedEye(ctx, 7.5, 18.4); } } },
  variagchief: { biped: { skin: "#6e4a30", torso: "#1c1c20", legs: "#3a1a14", boot: "#140e0a", belt: "#d8b040", mail: true, size: 1.3,
    head: (ctx) => { easterlingHelm(ctx); closedEye(ctx, 7.8, 18.4); }, keeps: easterlingHelm } },
  orcuruk: { biped: { skin: "#3e3a2e", torso: "#2c2d31", mail: true, legs: "#1a1a1a", belt: "#3a2a1c", size: 1.3,
    head: (ctx) => { urukHelm(ctx); closedEye(ctx, 7.5, 18.5); rect(ctx, 9.5, 18.5, 1, 3, "#e8e0c8"); },
    keeps: (ctx) => { urukHelm(ctx); for (let y = 16; y < 25; y += 2) for (let x = 11; x < 20; x += 2) rect(ctx, x, y, 1, 1, "#4c4f55"); } } },
  // The Black Orc: the Uruk-hai's body a size up, near black, in black mail.
  blackorc: { biped: { skin: "#2e2c2e", torso: "#1c1c20", mail: true, legs: "#0e0e11", belt: "#2a1414", size: 1.38,
    head: (ctx) => { blackOrcHelm(ctx); closedEye(ctx, 7.5, 18.5); rect(ctx, 9.5, 18.5, 1, 3, "#e8e0c8"); },
    keeps: (ctx) => { blackOrcHelm(ctx); for (let y = 16; y < 25; y += 2) for (let x = 11; x < 20; x += 2) rect(ctx, x, y, 1, 1, "#3a3a42"); } } },
  // Gundabad's orcs (2026-10-15), in the colours they were drawn alive in:
  // the Gundabad orc grey in rust-red mail, Bolg's bodyguard in black, and
  // Bolg pale grey under his white wolf-pelt.
  orcgundabad: { biped: { skin: "#6c7266", torso: "#6a4a34", mail: true, legs: "#2a2622", belt: "#5a3a24", size: 1.33,
    head: (ctx) => { gundabadHelm(ctx, "#55524e", "#8a8580"); closedEye(ctx, 7.5, 18.5); rect(ctx, 9.5, 18.5, 1, 3, "#e8e0c8"); },
    keeps: (ctx) => { gundabadHelm(ctx, "#55524e", "#8a8580"); for (let y = 16; y < 25; y += 2) for (let x = 11; x < 20; x += 2) rect(ctx, x, y, 1, 1, "#8e6a4c"); } } },
  orcbodyguard: { biped: { skin: "#4c4f48", torso: "#232326", mail: true, legs: "#121214", belt: "#5a1410", size: 1.46,
    head: (ctx) => { gundabadHelm(ctx, "#2c2c30", "#5c5c66"); closedEye(ctx, 7.5, 18.5); rect(ctx, 9.5, 18.5, 1, 3, "#e8e0c8"); },
    keeps: (ctx) => { gundabadHelm(ctx, "#2c2c30", "#5c5c66"); for (let y = 16; y < 25; y += 2) for (let x = 11; x < 20; x += 2) rect(ctx, x, y, 1, 1, "#44444c"); } } },
  orcbolg: { biped: { skin: "#8c8e86", torso: "#26262a", mail: true, legs: "#141416", belt: "#4a1a10", cloth: "#d8d4c6", size: 1.55,
    head: (ctx) => { bolgHelm(ctx); closedEye(ctx, 7.5, 18.5); rect(ctx, 9.5, 18.5, 1, 3, "#e8e0c8"); },
    keeps: (ctx) => { bolgHelm(ctx); rect(ctx, 10, 15, 11, 10, "#d8d4c6"); } } },
  // The Wood-elves of Mirkwood and their king (2026-10-07), in the colours
  // they were drawn alive in: the archer's leaf-green and the warden's
  // brown under their hoods, which are what is left of them; the guard in
  // silver mail, leaving his helm; the king in red and gold, leaving his
  // crown of leaves gone brown.
  woodelfarcher: { biped: { skin: "#ecd4b8", torso: "#4a7a32", legs: "#5a4228", boot: "#3a2814", belt: "#6a4622", cloth: "#3a5a26", size: 1.15,
    head: (ctx, st, sk) => { elfEars(ctx, sk); ell(ctx, 3.6, 20, 2.8, 4.6, aged("#3e6a2a", st)); closedEye(ctx, 8, 18.4); },
    keeps: (ctx) => { ell(ctx, 3.6, 20, 2.6, 4.2, "#2a481c"); } } },
  woodelfwarden: { biped: { skin: "#e8d0b4", torso: "#6e5232", legs: "#3e4a28", boot: "#2e2214", belt: "#3e6a2a", cloth: "#2a4a22", size: 1.15,
    head: (ctx, st, sk) => { elfEars(ctx, sk); ell(ctx, 3.6, 20, 2.8, 4.6, aged("#2a4a22", st)); closedEye(ctx, 8, 18.4); },
    keeps: (ctx) => { ell(ctx, 3.6, 20, 2.6, 4.2, "#1a3214"); } } },
  elvenguard: { biped: { skin: "#ecd6bc", torso: "#aab2ba", mail: true, legs: "#2a4a2c", boot: "#3a2a1a", belt: "#2e6a34", cloth: "#2a5a2e", size: 1.15,
    head: (ctx, st, sk) => { elfEars(ctx, sk); elfHelm(ctx); closedEye(ctx, 8, 18.4); }, keeps: elfHelm } },
  thranduil: { biped: { skin: "#f2dec8", torso: "#8a1c1c", legs: "#5a1010", boot: "#3a2414", belt: "#e0b040", cloth: "#b07a26", size: 1.2,
    head: (ctx, st, sk) => { ell(ctx, 3.4, 20, 2.6, 5.2, aged("#ecd48a", st)); elfEars(ctx, sk); leafCrown(ctx, aged("#4e8a2a", st), "#d0202a"); closedEye(ctx, 8, 18.4); },
    keeps: (ctx) => { leafCrown(ctx, "#7a5a30", "#6a1a1a"); } } },
  // The dead of the crypt under Sauron's temple (2026-10-08): a King's Man
  // in his sea-green mail, leaving his helm; one of the Charred Dead, black
  // with the embers going out in it. The shade gutters and the High Priest
  // rises, so neither leaves a body.
  kingsman: { biped: { skin: "#8a9a8c", torso: "#6e8478", mail: true, legs: "#34403a", boot: "#2a2a26", belt: "#5a4a30", cloth: "#2a4a44", size: 1.15,
    head: (ctx, st) => { ell(ctx, 3.6, 20, 2.8, 4.6, aged("#7a8e84", st)); rect(ctx, 1.2, 19.4, 2.4, 1.2, aged("#3a5a50", st)); closedEye(ctx, 8, 18.4); },
    keeps: (ctx) => { ell(ctx, 3.6, 20, 2.6, 4.2, "#5e7066"); } } },
  // Site 3 (2026-10-16): one of Arvedui's drowned men under the Ice Bay,
  // in the dark mail of the North with his sodden hood about his head.
  drowned: { biped: { skin: "#94a6a0", torso: "#5c6c72", mail: true, legs: "#2e3a3e", boot: "#262a2a", belt: "#4a3e30", cloth: "#2a3a44", size: 1.15,
    head: (ctx, st) => { ell(ctx, 3.6, 20, 2.9, 4.7, aged("#3a4a54", st)); rect(ctx, 5.6, 18.6, 1.6, 2.6, aged("#94a6a0", st)); closedEye(ctx, 8, 18.4); } } },
  // 10. Drúwaith Iaur (2026-10-16): a Wose, broad and brown in its grass
  // skirt, the leaves still in its dark hair.
  wose: { biped: { skin: "#6e4c2e", torso: "#5e6e30", legs: "#6a4a2c", boot: "#4a3220", belt: "#3e2c18", cloth: "#3e4c1e", size: 1.1,
    head: (ctx, st) => { ell(ctx, 3.6, 20, 2.8, 4.6, aged("#241a12", st)); closedEye(ctx, 8, 18.4); },
    keeps: (ctx) => { leafCrown(ctx, "#4e6e28", "#7a2a1a"); } } },
  charreddead: { biped: { skin: "#2a221e", torso: "#3a2a22", legs: "#2a201a", boot: "#1a1410", belt: "#5a2a14", size: 1.15,
    head: (ctx, st) => { ell(ctx, 3.6, 20, 2.6, 4.2, aged("#141010", st)); rect(ctx, 6, 19, 1, 1, aged("#c84a1a", st)); closedEye(ctx, 8, 18.4); } } },
  // The High Priest of Melkor, a living man since 2026-11-03 ("Remove the
  // rises from him"), so he leaves a body: black robes scorched to rags,
  // an ember-red cord at the waist, dark hair going grey.
  highpriest: { biped: { skin: "#b89878", torso: "#1c1614", legs: "#16110f", boot: "#0e0b0a", belt: "#a83a14", cloth: "#120e0c", size: 1.15,
    head: (ctx, st) => { ell(ctx, 3.6, 20, 2.8, 4.6, aged("#3a3632", st)); closedEye(ctx, 8, 18.4); } } },
  // The Blue Tower of Rhûn (2026-11-01): the acolyte in faded blue, black
  // haired; the Remade bare and grey, blue signs cut in it; the Blue Wizard
  // in deep sea-blue, white-haired and white-bearded.
  acolyte: { biped: { skin: "#b08a68", torso: "#3e5a86", legs: "#2a3448", boot: "#1a1c22", belt: "#8ab4e8", cloth: "#2a3e5e", size: 1.15,
    head: (ctx, st) => { ell(ctx, 3.6, 20, 2.8, 4.6, aged("#3a5280", st)); closedEye(ctx, 8, 18.4); } } },
  remade: { biped: { skin: "#8a9096", torso: "#8a9096", legs: "#3a3430", boot: "#22201c", belt: "#5aa0ff", size: 1.2,
    head: (ctx, st) => { ell(ctx, 3.6, 20, 2.6, 4.2, aged("#3a3e44", st)); rect(ctx, 12, 14, 1, 3, aged("#5aa0ff", st)); closedEye(ctx, 8, 18.4); } } },
  bluewizard: { biped: { skin: "#d8b8a0", torso: "#1e3e78", legs: "#132a56", boot: "#141820", belt: "#7ab0ff", cloth: "#16305e", size: 1.15,
    head: (ctx, st) => { ell(ctx, 3.6, 20, 2.8, 4.8, aged("#d8dce2", st)); rect(ctx, 6.5, 21.5, 3, 2, aged("#e4e8ec", st)); closedEye(ctx, 8, 18.4); } } },
  // A stone troll turns to stone, as every troll does, and the stone is what is left.
  stonetroll: { draw: (ctx, st) => {
    if (st < 2) { biped(ctx, STONE_TROLL, st); return; }
    biped(ctx, { ...STONE_TROLL, skin: "#8b8d86", torso: "#7e807a", legs: "#6f716b", belt: "#6a6c66", boot: "#62645e", head: undefined }, 0);
    line(ctx, [[11, 15], [15, 21], [12, 26]], "#55574f", 0.8);
    line(ctx, [[22, 16], [26, 19]], "#55574f", 0.8);
  } },
  huorn: { draw: huorn },
  // An Ent of Fangorn (2026-10-21) falls as the huorn does, a fifth as big
  // again, with the grey-green moss of its beard still by the leaves.
  ent: { draw: (ctx, st) => {
    ctx.save();
    ctx.translate(16, 22); ctx.scale(1.2, 1.2); ctx.translate(-16, -22);
    huorn(ctx, st);
    if (st < 2) { ell(ctx, 31, 24, 4, 2.4, aged("#6a7a4a", st)); line(ctx, [[29, 24], [27, 27]], aged("#4a5a34", st), 0.8); }
    ctx.restore();
  } },
  willow: { draw: willow },
  mumak: { draw: mumak },
  shelob: { draw: (ctx, st) => spider(ctx, st, { body: "#141216", dark: "#2c2a34", mark: "#8a8478", head: "#0b0a0d", eye: "#3a6a2a" }, 2.1) },
  // Ungoliant and Sauron (2026-10-26: "Add ungoliant and Sauron as
  // monsters but no specific spawn"): Shelob's body bigger again and black
  // over black, red-eyed; and the Dark Lord fallen in his black mail, a
  // size over Bolg, his helm on.
  ungoliant: { draw: (ctx, st) => spider(ctx, st, { body: "#08070a", dark: "#1c1a22", mark: "#4a3a5a", head: "#040306", eye: "#7a1010" }, 2.5) },
  // The Mirkwood spider (2026-10-27): the giant spider's body a size down
  // and brown, its eyes yellow.
  mirkwoodspider: { draw: (ctx, st) => spider(ctx, st, { body: "#3c3024", dark: "#241c14", mark: "#b09a6a", head: "#2a221a", eye: "#8a7020" }, 1.1) },
  sauron: { biped: { skin: "#3a3236", torso: "#141418", mail: true, legs: "#0a0a0c", belt: "#5a1a0a", size: 1.6,
    head: (ctx) => sauronHelm(ctx), keeps: sauronHelm } },
  dragon: { draw: (ctx, st) => dragon(ctx, st) },
  dragonlord: { draw: dragonLord },
  smaug: { draw: smaug },
  demon: { draw: demon },
  siren: { draw: siren },
  watcher: { draw: watcher },
  wereworm: { draw: wereworm },
  // --- 12. Belegost (2026-10-16): a dwarf shade falls as a dwarf does, in
  // the grey it walked in, and Azaghâl in his lord's crown; a cold-drake as
  // a dragon does, smaller, in its frost-grey hide.
  dwarfshade: { biped: { skin: "#a9b2b8", torso: "#6f7780", mail: true, legs: "#454a50", belt: "#596068", size: 1.0,
    head: (ctx, st) => { dwarfHelm(ctx); ell(ctx, 10.5, 20, 3.4, 3, aged("#c8ced2", st)); }, keeps: dwarfHelm } },
  azaghal: { biped: { skin: "#a9b2b8", torso: "#4f565e", mail: true, legs: "#2f3338", belt: "#c9a227", size: 1.05,
    head: (ctx, st) => { crown(ctx); ell(ctx, 11, 20, 3.8, 3.2, aged("#d8dde0", st)); }, keeps: crown } },
  colddrake: { draw: coldDrake },
  // --- end of 12. Belegost
  // The fell beast (2026-11-01), a Nazgûl's steed: a dragon's body, a
  // little smaller, in its naked grey hide.
  fellbeast: { draw: fellBeast },
  // --- The northern spots (2026-11-01): an old stone troll falls as a
  // stone troll does, a size up and greyer, and its remains are stone; the
  // white wolf as a winter wolf, bigger; and Scatha as a cold-drake, as big
  // as the dragon lord.
  oldtroll: { draw: (ctx, st) => {
    ctx.save();
    ctx.translate(16, 22); ctx.scale(1.15, 1.15); ctx.translate(-16, -22);
    if (st < 2) biped(ctx, { ...STONE_TROLL, skin: "#7c7a6c", torso: "#6a685c" }, st);
    else {
      biped(ctx, { ...STONE_TROLL, skin: "#8b8d86", torso: "#7e807a", legs: "#6f716b", belt: "#6a6c66", boot: "#62645e", head: undefined }, 0);
      line(ctx, [[11, 15], [15, 21], [12, 26]], "#55574f", 0.8);
    }
    ctx.restore();
  } },
  whitewolf: { quad: { body: "#f2f5f8", back: "#ccd6de", belly: "#ffffff", tail: "bushy", size: 1.5, snout: 3.4, fur: "#dde4ea" } },
  scatha: { draw: (ctx, st) => {
    ctx.save();
    ctx.translate(16, 20); ctx.scale(1.2, 1.2); ctx.translate(-16, -20);
    dragon(ctx, st, { g: "#4a5c6a", d: "#1e2a34", belly: "#cfd9e0", wing: "#36444e" });
    ctx.restore();
  } },
};

/** Kinds drawn as another kind's body. */
export const BODY_AS: Record<string, string> = {
  dwarfqueen: "dwarfking",
  // Longbottom's shed rat and shed kobold (empty-areas site 1, 2026-10-16)
  // are the rat and the kobold, and leave their bodies.
  shedrat: "rat",
  shedkobold: "kobold",
  // The gaoler of Dol Guldur's dungeons (2026-11-01) is a black orc,
  // bigger, with his keys, and falls as one.
  gaoler: "blackorc",
};

function bat(ctx: Ctx, st: number) {
  if (st < 2) {
    const b = aged("#4a4458", st), w = aged("#342f42", st);
    shadow(ctx, 16, 20, 12, 3);
    poly(ctx, [[16, 17], [4, 12], [3, 17], [6, 16], [7, 20], [10, 18], [16, 21]], w);
    poly(ctx, [[16, 17], [28, 12], [29, 17], [26, 16], [25, 20], [22, 18], [16, 21]], w);
    ell(ctx, 16, 19, 2.6, 3.4, b); ell(ctx, 16, 15.6, 2, 1.8, b);
    poly(ctx, [[14.6, 14.8], [14.2, 12.6], [15.6, 14.2]], b); poly(ctx, [[17.4, 14.8], [17.8, 12.6], [16.4, 14.2]], b);
    closedEye(ctx, 14.8, 15.4); rect(ctx, 16.4, 15.4, 1, 1, "#1b1410");
    if (st === 1) flies(ctx, 18, 9, 2);
  } else {
    line(ctx, [[16, 17], [5, 13]], BONE_D, 0.8); line(ctx, [[16, 17], [27, 13]], BONE_D, 0.8);
    line(ctx, [[8, 14], [7, 19]], BONE_D, 0.6); line(ctx, [[24, 14], [25, 19]], BONE_D, 0.6);
    line(ctx, [[16, 16], [16, 21]], BONE, 1); skull(ctx, 16, 15.6, 1.6);
  }
}

/** A spider body's colours: the abdomen, the legs, the marks, the head and the eyes. */
type SpiderHide = { body: string; dark: string; mark: string; head: string; eye: string };
const SPIDER_HIDE: SpiderHide = { body: "#3a2a42", dark: "#5e4a6a", mark: "#c89656", head: "#2a1e30", eye: "#5a1a1a" };

/**
 * A dead spider draws its legs in over itself. Shelob's is the same body
 * half as big again in her own black (2026-10-01: "Implement the entire
 * proposal in the background"), so the hide and the size are arguments.
 */
function spider(ctx: Ctx, st: number, hide: SpiderHide = SPIDER_HIDE, size = 1.45) {
  ctx.save();
  ctx.translate(16, 20); ctx.scale(size, size); ctx.translate(-16, -20);
  const body = aged(hide.body, st), dark = aged(hide.dark, st), mark = aged(hide.mark, st);
  if (st < 2) {
    shadow(ctx, 16, 23, 12, 4);
    ell(ctx, 16, 21, 8, 6.5, body);
    for (const [a, b] of [[10, 14], [13, 11], [19, 11], [22, 14]]) {
      const out = a < 16 ? -3.5 : 3.5, back = a < 16 ? 1 : -1;
      line(ctx, [[a, 18], [a + out, b], [a + back, b - 2.5], [16 + (a - 16) * 0.35, 12.5]], dark, 2.2);
    }
    ell(ctx, 16, 14.5, 4, 3.2, aged(hide.head, st));
    rect(ctx, 14.2, 13.8, 1.2, 1, hide.eye); rect(ctx, 17.2, 13.8, 1.2, 1, hide.eye);
    for (let i = 0; i < 3; i++) poly(ctx, [[16, 19 + i * 2.6], [13.8, 21 + i * 2.6], [18.2, 21 + i * 2.6]], mark);
    if (st === 1) flies(ctx, 22, 10, 3);
  } else {
    // The husk: the carapace outlasts what was in it.
    shadow(ctx, 16, 23, 10, 3);
    ell(ctx, 16, 21, 7.4, 6, "rgba(120,96,110,.75)");
    ell(ctx, 16, 21, 5.6, 4.4, "rgba(60,44,58,.55)");
    line(ctx, [[12, 18], [15, 21], [13, 24]], "rgba(230,215,200,.6)", 0.7);
    for (const [a, b] of [[10, 16], [22, 16], [11, 26], [21, 26]]) line(ctx, [[16 + (a - 16) * 0.5, (b + 21) / 2], [a, b]], "rgba(90,70,86,.9)", 0.9);
  }
  ctx.restore();
}

/** A dragon body's colours: the hide, its shadow, the belly and the wing. */
type DragonHide = { g: string; d: string; belly: string; wing: string };
const DRAGON_HIDE: DragonHide = { g: "#3e7a3a", d: "#2a5a2a", belly: "#c8b878", wing: "#2e5e2e" };

/** Head to the right on a long neck, one wing flung up, tail curled left. */
function dragon(ctx: Ctx, st: number, hide: DragonHide = DRAGON_HIDE) {
  const g = aged(hide.g, st), d = aged(hide.d, st), belly = aged(hide.belly, st), wing = aged(hide.wing, st);
  if (st < 2) {
    shadow(ctx, 16, 26, 26, 6);
    line(ctx, [[2, 24], [-8, 26], [-14, 21], [-10, 17]], d, 3.2);
    poly(ctx, [[10, 16], [-2, -6], [6, -2], [12, -10], [16, -1], [22, -6], [22, 14]], wing);
    line(ctx, [[10, 16], [-2, -6]], d, 1.2); line(ctx, [[14, 14], [12, -10]], d, 1.2); line(ctx, [[18, 14], [22, -6]], d, 1.2);
    ell(ctx, 12, 21, 13, 7.5, g); ell(ctx, 12, 24.5, 10, 3, belly);
    for (const x of [3, 8, 14, 20]) line(ctx, [[x, 26], [x - 1, 31]], d, 2);
    line(ctx, [[24, 18], [32, 14], [38, 16]], g, 4.2);
    ell(ctx, 41, 17, 5, 3.4, g); rect(ctx, 44, 17.4, 3.5, 2, g);
    poly(ctx, [[39, 14.5], [36, 9.5], [41, 13.8]], "#d8cfb8");
    xEye(ctx, 41.5, 15.5);
    if (st === 1) { flies(ctx, 10, 6, 5); flies(ctx, 36, 8, 3); }
  } else {
    shadow(ctx, 16, 26, 22, 4);
    line(ctx, [[2, 23], [-8, 25], [-14, 21], [-10, 17]], BONE_D, 1.4);
    line(ctx, [[0, 21], [24, 20], [32, 15], [37, 16]], BONE, 1.8);
    for (let x = 4; x <= 20; x += 2.6) line(ctx, [[x, 14], [x + 1, 20], [x, 27]], BONE, 1.1);
    line(ctx, [[10, 16], [-2, -6]], BONE_D, 0.9); line(ctx, [[14, 14], [12, -10]], BONE_D, 0.9); line(ctx, [[18, 14], [22, -6]], BONE_D, 0.9);
    skull(ctx, 41, 17, 4); rect(ctx, 43.5, 17.2, 4, 2.2, BONE); poly(ctx, [[39, 14.5], [36, 9.5], [41, 13.8]], BONE_D);
  }
}

/**
 * A cold-drake's body (site 12, Belegost, 2026-10-16): a dragon's, drawn
 * smaller, in its grey-blue hide, the fin along its back where a dragon's
 * wing would be in the hide's own shadow.
 */
function coldDrake(ctx: Ctx, st: number) {
  ctx.save();
  ctx.translate(16, 20);
  ctx.scale(0.85, 0.85);
  ctx.translate(-16, -20);
  dragon(ctx, st, { g: "#55697a", d: "#26323d", belly: "#d9e2e8", wing: "#3e4c58" });
  ctx.restore();
}

/**
 * The dragon lord's body (2026-09-26: "Add a dragon lord at the very top"):
 * a dragon's, in the lord's own dark red hide and gold belly, drawn a fifth
 * as big again about the tile it fell on, with its crown knocked off beside
 * its head. The crown is gold and does not rot, so it is still lying there
 * by the bones when everything else has gone.
 */
function dragonLord(ctx: Ctx, st: number) {
  ctx.save();
  ctx.translate(16, 20);
  ctx.scale(1.2, 1.2);
  ctx.translate(-16, -20);
  dragon(ctx, st, { g: "#7a2228", d: "#4a1218", belly: "#d9a441", wing: "#3a1a1e" });
  const gold = "#e0b83a";
  rect(ctx, 43, 7, 7, 2.2, gold);
  for (const x of [43, 46, 49]) poly(ctx, [[x, 7], [x + 0.5, 3.8], [x + 1, 7]], gold);
  rect(ctx, 45.6, 7.4, 1.4, 1.2, "#c02020");
  ctx.restore();
}

/**
 * Smaug's body (2026-10-27: "add smaug as a monster, with no specific spawn
 * yet"): a dragon's, red-gold, drawn a third as big again about the tile he
 * fell on, with the gems of his belly scattered round him. Gems do not rot,
 * so they lie there by the bones when everything else has gone.
 */
function smaug(ctx: Ctx, st: number) {
  ctx.save();
  ctx.translate(16, 20);
  ctx.scale(1.35, 1.35);
  ctx.translate(-16, -20);
  dragon(ctx, st, { g: "#a8401c", d: "#5a1c0e", belly: "#f0c850", wing: "#6a2410" });
  const gems = ["#f4fbff", "#e0303a", "#3aa0e0", "#40c060"];
  [[4, 28], [9, 30], [15, 29], [21, 31], [26, 28], [-2, 27], [12, 32]].forEach(([x, y], i) => rect(ctx, x, y, 1.2, 1.2, gems[i % gems.length]));
  ctx.restore();
}

/**
 * The fell beast's body (2026-11-01; redrawn the same day, with no
 * forelegs and a bat's wings): fallen on its side, one great wing spread
 * over the ground with its four long fingers, its two hind legs drawn up,
 * the long neck out and the beaked head on the ground. The bones are the
 * fingers of the wing, the spine and the beaked skull.
 */
function fellBeast(ctx: Ctx, st: number) {
  const g = aged("#3b3740", st), d = aged("#211e25", st), web = aged("#4c4456", st), belly = aged("#6a6270", st);
  if (st < 2) {
    shadow(ctx, 16, 26, 26, 6);
    poly(ctx, [[12, 18], [4, -4], [-6, 2], [-10, 12], [-6, 20], [6, 24]], web);
    for (const [x, y] of [[4, -4], [-6, 2], [-10, 12], [-6, 20]] as const) line(ctx, [[12, 16], [x, y]], d, 1);
    line(ctx, [[12, 18], [8, 6], [12, 16]], d, 2);
    line(ctx, [[2, 24], [-10, 28], [-16, 25]], d, 2.4);
    ell(ctx, 12, 21, 10, 6, g); ell(ctx, 12, 24, 8, 2.4, belly);
    for (const x of [9, 15]) line(ctx, [[x, 25], [x - 2, 30], [x + 1, 31]], d, 2);
    line(ctx, [[21, 18], [29, 15], [35, 17]], g, 3.4);
    ell(ctx, 38, 17, 3.6, 2.8, g);
    poly(ctx, [[40, 15.6], [48, 18], [48.4, 20], [40, 19]], "#a49a86");
    poly(ctx, [[36, 15], [31, 11], [37.5, 14.2]], "#a49a86");
    xEye(ctx, 38.4, 16.4);
    if (st === 1) { flies(ctx, 8, 4, 5); flies(ctx, 36, 10, 3); }
  } else {
    shadow(ctx, 16, 26, 22, 4);
    for (const [x, y] of [[4, -4], [-6, 2], [-10, 12], [-6, 20]] as const) line(ctx, [[12, 16], [x, y]], BONE_D, 0.9);
    line(ctx, [[-14, 25], [0, 22], [24, 20], [31, 16], [35, 17]], BONE, 1.6);
    for (let x = 6; x <= 18; x += 2.8) line(ctx, [[x, 15], [x + 1, 20], [x, 26]], BONE, 1);
    skull(ctx, 38, 17, 3.4);
    poly(ctx, [[40, 15.8], [47, 18], [47.4, 19.6], [40, 18.8]], BONE_D);
  }
}

/** A demon cools to a husk, and leaves a burn and the skull that would not burn. */
function demon(ctx: Ctx, st: number) {
  const red = st === 1 ? "#2c1a18" : "#9a1e1e", dark = st === 1 ? "#1a0f0e" : "#5a0e0e", rune = st === 1 ? "rgba(255,140,40,.55)" : "#ffb020";
  if (st < 2) {
    shadow(ctx, 16, 22, 22, 7);
    poly(ctx, [[14, 14], [-6, 2], [-8, 12], [-2, 11], [-4, 19], [4, 16], [12, 22]], dark);
    poly(ctx, [[14, 26], [-6, 38], [-8, 28], [-2, 29], [-4, 21], [4, 24], [12, 18]], dark);
    ctx.save();
    ctx.translate(16, 20); ctx.scale(1.45, 1.45); ctx.translate(-16, -20);
    line(ctx, [[12, 15], [9, 9], [6, 6]], red, 2.8); line(ctx, [[12, 25], [15, 31], [18, 32]], red, 2.8);
    rect(ctx, 20, 16, 9, 3.6, red); rect(ctx, 20, 20.4, 9, 3.6, red);
    rect(ctx, 10, 14.5, 11, 11, red);
    line(ctx, [[12, 16], [15, 20], [12, 24]], rune, 1); line(ctx, [[18, 16], [15, 20], [18, 24]], rune, 1);
    ell(ctx, 6.5, 20, 4.2, 4.2, red);
    poly(ctx, [[4, 16.5], [1, 11], [6, 16]], "#e8dcc8"); poly(ctx, [[4, 23.5], [1, 29], [6, 24]], "#e8dcc8");
    const eye = st === 1 ? "#3a2a20" : "#ffcc40";
    rect(ctx, 7.5, 18.5, 1.5, 1, eye); rect(ctx, 7.5, 21, 1.5, 1, eye);
    ctx.restore();
  } else {
    const g = ctx.createRadialGradient(14, 20, 2, 14, 20, 20);
    g.addColorStop(0, "rgba(20,12,10,.85)"); g.addColorStop(0.6, "rgba(40,24,18,.5)"); g.addColorStop(1, "rgba(40,24,18,0)");
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.ellipse(14, 20, 20, 13, 0, 0, Math.PI * 2); ctx.fill();
    for (const [x, y] of [[8, 15], [20, 24], [16, 13], [23, 17]]) rect(ctx, x, y, 1, 1, "rgba(255,150,50,.8)");
    skull(ctx, 8, 20, 4, "#3a302a");
    poly(ctx, [[5, 17], [1, 11], [7, 16]], "#d8ccb4"); poly(ctx, [[5, 23], [1, 29], [7, 24]], "#d8ccb4");
  }
}

/** A siren floats, goes back to the sea as foam, and leaves a few scales and a comb. */
function siren(ctx: Ctx, st: number) {
  if (st === 0) {
    const skin = "#bfe0dc", tail = "#2e8a86", hair = "#1f6a66";
    poly(ctx, [[18, 19], [28, 16], [31, 12], [32, 18], [31, 25], [28, 22], [18, 23]], tail);
    for (let x = 20; x < 28; x += 2.2) rect(ctx, x, 20, 1, 1, "#58b8b0");
    rect(ctx, 10, 17, 9, 6, skin);
    ell(ctx, 6.5, 20, 4, 4, skin); ell(ctx, 3.5, 20, 3.4, 5.5, hair);
    line(ctx, [[11, 17], [9, 12]], skin, 2); line(ctx, [[11, 23], [9, 28]], skin, 2);
    closedEye(ctx, 7.5, 18.6);
    ctx.lineWidth = 0.7; ctx.strokeStyle = "rgba(255,255,255,.5)";
    ctx.beginPath(); ctx.ellipse(16, 20, 16, 8, 0, 0, Math.PI * 2); ctx.stroke();
  } else if (st === 1) {
    for (const [x, y, r] of [[8, 20, 3], [13, 18, 2.4], [16, 22, 3], [21, 19, 2.2], [26, 21, 2.6], [11, 23, 1.6], [24, 16, 1.4]]) ell(ctx, x, y, r, r * 0.7, "rgba(245,250,250,.85)");
    rect(ctx, 18, 20, 1, 1, "#58b8b0"); rect(ctx, 22, 22, 1, 1, "#58b8b0");
  } else {
    for (const [x, y] of [[9, 19], [14, 22], [19, 18], [23, 21], [12, 17]]) { rect(ctx, x, y, 1.4, 1, "#7fd6cc"); rect(ctx, x, y, 0.6, 0.5, "#ffffff"); }
    poly(ctx, [[15, 24], [19, 24], [19, 25.4], [15, 25.4]], "#e0c89a");
    for (let x = 15.3; x < 19; x += 0.9) rect(ctx, x, 25.4, 0.5, 1.4, "#e0c89a");
  }
}

/**
 * The Watcher in the Water, dead (2026-10-08): its arms lie limp across the
 * water in a dark slick; then the slick and a few pale ends; then foam.
 */
function watcher(ctx: Ctx, st: number) {
  const arm = aged("#6f8f62", st), dark = aged("#46613f", st);
  if (st === 0) {
    ell(ctx, 16, 21, 12, 5, "rgba(13,29,42,.85)");
    line(ctx, [[16, 21], [8, 17], [3, 19], [2, 16]], dark, 2.4);
    line(ctx, [[16, 21], [24, 26], [29, 24], [31, 27]], dark, 2.4);
    line(ctx, [[16, 21], [26, 16], [30, 13]], arm, 2.6);
    line(ctx, [[16, 21], [9, 26], [5, 28]], arm, 2.6);
    for (const [x, y] of [[10, 17.5], [25, 17], [11, 25], [22, 24]]) rect(ctx, x, y, 1, 1, "#d6e6c0");
  } else if (st === 1) {
    ell(ctx, 16, 21, 9, 3.6, "rgba(13,29,42,.6)");
    line(ctx, [[14, 21], [8, 19]], arm, 1.6); line(ctx, [[18, 21], [25, 18]], arm, 1.6);
  } else {
    for (const [x, y, r] of [[10, 20, 2.4], [15, 22, 2.8], [21, 19, 2], [25, 22, 2.2]]) ell(ctx, x, y, r, r * 0.7, "rgba(235,245,240,.8)");
  }
}

/**
 * A were-worm, dead (2026-10-09): half out of its hole, its length slumped
 * along the ground with the head on its side; then flattened and dull with
 * flies; then a long run of ribs and a horned skull by the earth it came
 * out of.
 */
function wereworm(ctx: Ctx, st: number) {
  ell(ctx, 6, 22, 6, 3, "#5b4630");
  ell(ctx, 6, 22, 3.6, 1.8, "#1b140d");
  if (st < 2) {
    const body = aged("#7a6a52", st), dark = aged("#54493a", st), belly = aged("#b3a07a", st);
    shadow(ctx, 17, 23, 13, 3);
    for (let i = 0; i <= 8; i++) {
      const x = 6 + i * 2.6, y = 22 - Math.sin(i * 0.7) * 1.6, r = 3.6 - i * 0.12;
      ell(ctx, x, y, r, r * 0.8, i % 2 ? body : dark);
      ell(ctx, x, y + r * 0.45, r * 0.7, r * 0.3, belly);
    }
    ell(ctx, 29, 21, 3.6, 2.6, body);
    rect(ctx, 30, 22.4, 2, 0.8, "#1b140d");
    closedEye(ctx, 28.6, 20.2);
    if (st === 1) flies(ctx, 20, 15, 3);
  } else {
    line(ctx, [[7, 22], [28, 21]], BONE_D, 1.2);
    for (let x = 9; x <= 25; x += 2.4) line(ctx, [[x, 19.4], [x, 24]], BONE, 0.7);
    skull(ctx, 29, 21, 1.8);
    line(ctx, [[30, 19.6], [32, 18]], BONE_D, 0.7);
  }
}

/**
 * A crebain on its back, wings out flat and its feet in the air; then dull
 * and flattened; then a few black feathers and the little bones (2026-10-01).
 */
function crebain(ctx: Ctx, st: number) {
  if (st < 2) {
    const b = aged("#1d1b22", st), w = aged("#2b2833", st);
    shadow(ctx, 16, 21, 9, 2.5);
    poly(ctx, [[14, 19], [4, 15], [5, 19], [3, 21], [14, 22]], w);
    poly(ctx, [[18, 19], [28, 15], [27, 19], [29, 21], [18, 22]], w);
    ell(ctx, 16, 20, 3.4, 2.6, b);
    ell(ctx, 16, 16.4, 2, 1.8, b);
    poly(ctx, [[15.4, 15], [16.6, 15], [16, 12.6]], aged("#4a4038", st));
    line(ctx, [[15, 22], [14.4, 25]], "#3a3530", 0.6); line(ctx, [[17, 22], [17.6, 25]], "#3a3530", 0.6);
    closedEye(ctx, 15.2, 16.4);
    if (st === 1) flies(ctx, 19, 11, 2);
  } else {
    for (const [x, y, r] of [[8, 18, 0.3], [24, 19, -0.4], [12, 23, 0.8], [21, 23, -0.2]]) line(ctx, [[x, y], [x + 4 * Math.cos(r), y + 4 * Math.sin(r)]], "#2b2833", 1.2);
    line(ctx, [[16, 17], [16, 22]], BONE, 0.9); skull(ctx, 16, 16, 1.4);
  }
}

/**
 * A huorn falls as a tree falls, a log with its roots out one end and its
 * leaves the other; the leaves brown and drop, and what is left is a grey
 * rotten log with a shelf of fungus on it (2026-10-01).
 */
function huorn(ctx: Ctx, st: number) {
  const bark = st === 2 ? "#6a645c" : aged("#5e4e3c", st), dark = st === 2 ? "#4a4640" : aged("#3e3226", st);
  shadow(ctx, 16, 24, 22, 4);
  for (const [dx, dy] of [[-4, -4], [-5, 0], [-4, 4]]) line(ctx, [[-2, 20], [-2 + dx, 20 + dy]], dark, 1.6);
  poly(ctx, [[-2, 16], [34, 17.5], [34, 23.5], [-2, 25]], bark);
  for (const x of [4, 12, 20, 28]) line(ctx, [[x, 17], [x + 1, 24]], dark, 0.7);
  if (st < 2) {
    const leaf = st === 1 ? "#6a5a2a" : "#2e4a26", leaf2 = st === 1 ? "#7a6a3a" : "#46663a";
    ell(ctx, 40, 18, 9, 7, leaf); ell(ctx, 44, 24, 6, 5, leaf2); ell(ctx, 37, 26, 5, 4, leaf);
    if (st === 0) { rect(ctx, 8, 20, 1.5, 1, "#f0b040"); rect(ctx, 12, 20, 1.5, 1, "#f0b040"); }
  } else {
    ell(ctx, 16, 16.5, 4, 1.6, "#c8a878"); ell(ctx, 22, 17, 2.5, 1.2, "#c8a878");
  }
}

/**
 * Old Man Willow falls as the huorn does, half as big again: a great grey
 * log with its roots out one end and its weeping crown the other, the
 * leaves yellowing and dropping, and a rotten log at the last with the
 * cracks still dark in it (2026-10-12).
 */
function willow(ctx: Ctx, st: number) {
  const bark = st === 2 ? "#6a685e" : aged("#77756a", st), dark = st === 2 ? "#4a4842" : aged("#4a483f", st);
  ctx.save();
  ctx.translate(16, 22); ctx.scale(1.5, 1.5); ctx.translate(-16, -22);
  shadow(ctx, 16, 24, 22, 4);
  for (const [dx, dy] of [[-4, -4], [-5, 0], [-4, 4]]) line(ctx, [[-2, 20], [-2 + dx, 20 + dy]], dark, 1.8);
  poly(ctx, [[-2, 15.5], [34, 17], [34, 24], [-2, 25.5]], bark);
  for (const x of [4, 12, 20, 28]) line(ctx, [[x, 17], [x + 1, 24]], dark, 0.7);
  line(ctx, [[9, 19], [15, 20], [21, 19.5]], "#17140f", 0.9);
  if (st < 2) {
    const leaf = st === 1 ? "#8a8a4a" : "#7f9064", leaf2 = st === 1 ? "#9a9a5a" : "#a6b588";
    ell(ctx, 40, 18, 9, 7, leaf); ell(ctx, 44, 24, 6, 5, leaf2); ell(ctx, 37, 26, 5, 4, leaf);
    for (const y of [14, 19, 24, 28]) line(ctx, [[36, y], [46, y + 2]], leaf2, 0.6);
    if (st === 0) { rect(ctx, 8, 18, 1.5, 1, "#d8a038"); rect(ctx, 12, 18, 1.5, 1, "#d8a038"); }
  } else {
    ell(ctx, 16, 16, 4, 1.6, "#c8a878"); ell(ctx, 22, 16.5, 2.5, 1.2, "#c8a878");
  }
  ctx.restore();
}

/**
 * A mûmak goes over on its side, a hill of grey hide with its tusks still
 * up and the red of the war-tower's cloth beside it; it rots like anything
 * that size, and the ivory outlasts the bones (2026-10-01).
 */
function mumak(ctx: Ctx, st: number) {
  quad(ctx, { body: "#7d7570", back: "#5a534f", belly: "#9a928c", ear: "#5a534f", tail: "thin", tailc: "#5a534f", size: 2.3, snout: 4.4 }, st);
  ctx.save();
  ctx.translate(16, 26); ctx.scale(2.3, 2.3); ctx.translate(-16, -26);
  line(ctx, [[29, 21.5], [33, 22.5], [35.5, 19.5]], "#efe8d2", 1.1);
  line(ctx, [[28.5, 22.5], [31.5, 24.5], [34, 23]], "#efe8d2", 1);
  if (st < 2) poly(ctx, [[8, 15], [20, 14.5], [21, 17.5], [9, 18]], st === 1 ? "#6a2a22" : "#a82020");
  ctx.restore();
}

/**
 * A player's body. Asked for on 2026-10-07: "make player corpses more
 * distinctive." Until then it was a monster's biped in a tunic of the
 * player's colour, a size smaller than most, and the skull a death drops
 * lands in the middle of the tile on top of it, so all that showed of a
 * dead player round the skull was a head and a pair of boots: a bandit's
 * body said as much.
 *
 * Now it is three things no monster's body is:
 *
 *   - their cloak, spread out under them in their own colour and flaring
 *     past their feet, so the colour shows all round the skull and says
 *     whose it is from across a field, the way their name and their dot on
 *     the minimap do;
 *   - their race's shape and hair, the ones they walked about in (see
 *     raceLook in sprites.ts): an elf long and slight with hair fanned out and
 *     pointed ears, a dwarf short and broad with a beard down the chest, a
 *     hobbit small with curls and bare feet, and a man as a man;
 *   - and, at the end, a marker at the head: a wooden cross with a strip of
 *     their colour tied to it, over the bones on what is left of the cloak.
 *     Nothing else that dies is marked.
 *
 * Rotting takes the flesh as it takes a monster's, toward the olive and
 * down, but cloth keeps its colour longer than skin does, so the cloak is
 * only dulled and stays theirs to the last stage.
 */
function playerBody(ctx: Ctx, st: number, color: string, race: string | undefined, hair = 0) {
  const c = /^#[0-9a-f]{6}$/i.test(color) ? color : "#8a8a8a";
  const look = raceLook(race, hair);
  // Lying down, the race's height is the body's length (across, here) and
  // its breadth is its breadth (down), about the middle of the body.
  const len = 1.15 * look.sy, wide = 1.15 * look.sx;
  ctx.save();
  ctx.translate(16, 20); ctx.scale(len, wide); ctx.translate(-16, -20);
  shadow(ctx, 18, 22, 17, 6);
  playerCloak(ctx, st, c);
  if (st < 2) playerFlesh(ctx, st, c, look);
  else playerBones(ctx, look);
  ctx.restore();
  if (st === 1) flies(ctx, 17, 9);
  if (st === 2) graveMarker(ctx, c);
}

/**
 * What a player's colour has become on their cloak by this stage: a shade
 * darker than the tunic, so that the body shows on it, and dulled but
 * never rotted.
 */
export function cloakColor(c: string, st: number): string {
  const cloak = mix(c, "#000000", 0.3);
  return st === 0 ? cloak : st === 1 ? mix(cloak, "#000000", 0.2) : mix(mix(cloak, ROT, 0.2), "#000000", 0.1);
}

/** The cloak, from the shoulders out past the feet: whole while there is somebody on it, torn at the hem when there is not. */
function playerCloak(ctx: Ctx, st: number, c: string) {
  const cloth = cloakColor(c, st), hem = mix(cloth, "#000000", 0.35);
  if (st < 2) {
    poly(ctx, [[9, 12.5], [22, 8.5], [35, 9.5], [36.5, 30.5], [22, 31.5], [9, 27.5]], hem);
    poly(ctx, [[10, 13.5], [22, 9.5], [34, 10.5], [35.5, 29.5], [22, 30.5], [10, 26.5]], cloth);
    line(ctx, [[23, 11], [33, 12]], mix(cloth, "#ffffff", 0.18), 1);
    line(ctx, [[23, 29], [33, 28]], hem, 1);
    return;
  }
  poly(ctx, [[9, 13], [21, 9.5], [27, 11], [30, 9.5], [33, 12.5], [31, 16], [34, 20], [31, 24], [33, 28], [27, 28.5], [24, 31], [20, 29], [9, 27]], hem);
  poly(ctx, [[10, 14], [21, 10.5], [27, 12], [29.5, 11], [32, 13], [30, 16], [33, 20], [30, 24], [31.5, 27.5], [27, 27.5], [24, 30], [20, 28], [10, 26]], cloth);
}

/** Somebody on their back on their cloak, head to the left, arms flung out, eyes shut. */
function playerFlesh(ctx: Ctx, st: number, c: string, look: RaceLook) {
  const skin = aged(look.skin, st), tunic = aged(c, st), legs = aged("#5a4630", st), hair = aged(look.hair, st);
  const edge = mix(tunic, "#000000", 0.55);
  line(ctx, [[12, 15], [9, 9], [6, 7]], tunic, 2.6);
  line(ctx, [[12, 25], [15, 30], [18, 31]], tunic, 2.6);
  rect(ctx, 5, 6, 2, 2, skin); rect(ctx, 17.5, 30, 2, 2, skin);
  rect(ctx, 20, 16, 9, 3.4, legs); rect(ctx, 20, 20.6, 9, 3.4, legs);
  if (look.bareFeet) {
    // A hobbit's feet: bare, big, and hairy on top.
    ell(ctx, 30.5, 17.6, 2.4, 1.9, skin); ell(ctx, 30.5, 22.4, 2.4, 1.9, skin);
    rect(ctx, 29.5, 17, 1, 1, hair); rect(ctx, 29.5, 22, 1, 1, hair);
  } else {
    rect(ctx, 28, 15.6, 2.6, 4, aged("#2a2118", st)); rect(ctx, 28, 20.4, 2.6, 4, aged("#2a2118", st));
  }
  rect(ctx, 9.5, 14, 12, 12, edge);
  rect(ctx, 10, 14.5, 11, 11, tunic);
  rect(ctx, 19, 14.5, 1.4, 11, aged("#4a3018", st));
  if (look.longHair) {
    // An elf's long hair, fanned out on the ground behind the head.
    poly(ctx, [[6, 15.5], [1.5, 14], [-1.5, 17], [-2, 20], [-1.5, 23], [1.5, 26], [6, 24.5]], hair);
  }
  ell(ctx, 6.5, 20, 4.2, 4.2, skin);
  if (look.ears) {
    poly(ctx, [[5, 16.3], [3.5, 12.6], [7.4, 16.2]], skin);
    poly(ctx, [[5, 23.7], [3.5, 27.4], [7.4, 23.8]], skin);
  }
  if (look.curls) {
    for (const [x, y] of [[3.4, 17], [2.8, 20], [3.4, 23], [5.2, 15.6], [5.2, 24.4]]) ell(ctx, x, y, 1.5, 1.5, hair);
  } else {
    ell(ctx, 4.4, 20, 2.4, 4, hair);
  }
  closedEye(ctx, 7.6, 18.4);
  closedEye(ctx, 7.6, 21.2);
  if (look.beard) {
    // A dwarf's beard, from the chin down the chest, and its forked end.
    const beard = aged(look.beard, st);
    ell(ctx, 12, 20, 3.8, 3.2, beard);
    poly(ctx, [[13, 17.6], [18, 18.6], [16, 20], [18, 21.4], [13, 22.4]], beard);
  }
}

/** What is left on the cloak: the bones, in the race's shape, and what of a people outlasts the flesh. */
function playerBones(ctx: Ctx, look: RaceLook) {
  line(ctx, [[10, 20], [20, 20]], BONE, 1.4);
  ribs(ctx, 11, 18, 20, 4, BONE);
  line(ctx, [[12, 15], [9, 9], [6, 7]], BONE_D, 1.1); line(ctx, [[12, 25], [15, 30], [18, 31]], BONE_D, 1.1);
  line(ctx, [[20, 18], [29, 17.5]], BONE, 1.2); line(ctx, [[20, 22], [29, 22.5]], BONE, 1.2);
  if (look.longHair) poly(ctx, [[5, 16], [1, 14.5], [-1.5, 20], [1, 25.5], [5, 24]], mix(look.hair, "#8a8270", 0.35));
  skull(ctx, 6.5, 20, 3.6);
  if (look.beard) poly(ctx, [[9.5, 18], [15, 19], [13.5, 20], [15, 21], [9.5, 22]], mix(look.beard, "#8a8270", 0.3));
}

/**
 * The marker over a player's remains: a wooden cross at their head with a
 * strip of their own colour tied to it, standing up off the ground. It is
 * in the tile's own units and not the race's, so every people's is the same
 * size.
 */
function graveMarker(ctx: Ctx, c: string) {
  const wood = "#7a5a36", dark = "#3e2c18";
  // Tall enough to stand up over the skull a death leaves on the tile, as
  // anything standing does, and edged dark so it reads against grass.
  ell(ctx, -1.5, 21.5, 3.6, 1.3, "rgba(0,0,0,.3)");
  rect(ctx, -3.6, -1.4, 4.2, 23.4, dark);
  rect(ctx, -8.4, 2.6, 13.8, 4.2, dark);
  rect(ctx, -3, -0.8, 3, 22.2, wood);
  rect(ctx, -7.8, 3.2, 12.6, 3, wood);
  // The strip of cloth, knotted at the crossing and hanging off the arm.
  const shade = mix(c, "#000000", 0.35);
  rect(ctx, -3.6, 2.4, 4.2, 4.8, shade);
  rect(ctx, -3, 3, 3, 3.6, c);
  poly(ctx, [[0.6, 4.4], [6.6, 7], [5.4, 9.6], [7.4, 13.6], [3, 11.6], [0.2, 7]], shade);
  poly(ctx, [[0.8, 5], [5.8, 7.4], [4.6, 9.6], [6, 12.2], [3, 10.8], [0.8, 6.8]], c);
}

/** The stage a body item is at: 0 fresh, 1 rotting, 2 remains. */
export function bodyStage(id: number): number {
  return id === BODY_ROTTING ? 1 : id === BODY_REMAINS ? 2 : 0;
}

/**
 * Draws a body into ctx, whose origin is the top-left of the tile it lies
 * on. A kind is a monster's; with none it is a player's, in their colour and
 * their race's shape (none is a man's, as an unchosen character is).
 * Kept apart from the canvas so that it can be drawn into anything, a
 * test's recorder included.
 */
export function drawBody(ctx: Ctx, stage: number, kind: string | undefined, color: string | undefined, race?: string, hair = 0) {
  ctx.save();
  if (!kind) {
    playerBody(ctx, stage, color ?? "", race, hair);
  } else {
    const k = KINDS[BODY_AS[kind] ?? kind];
    if (k && "quad" in k) quad(ctx, k.quad, stage);
    else if (k && "biped" in k) biped(ctx, k.biped, stage);
    else if (k) k.draw(ctx, stage);
  }
  ctx.restore();
}

/** Every kind a body can be drawn for, by the key the server sends. */
export function bodyKinds(): string[] {
  return [...Object.keys(KINDS), ...Object.keys(BODY_AS)];
}

const cache = new Map<string, HTMLCanvasElement>();

/**
 * A body item as a sprite: three tiles square with the tile it lies on in
 * the middle, so that the renderer's centring on the tile puts it where it
 * fell and lets a big body overhang the tiles round it.
 */
export function bodySprite(id: number, kind: string | undefined, color: string | undefined, race?: string, hair = 0): HTMLCanvasElement {
  const key = `${id}|${kind ?? ""}|${color ?? ""}|${race ?? ""}|${hair}`;
  let c = cache.get(key);
  if (!c) {
    c = document.createElement("canvas");
    c.width = 96; c.height = 96;
    const ctx = keptContext(c);
    ctx.imageSmoothingEnabled = false;
    ctx.translate(32, 32);
    drawBody(ctx, bodyStage(id), kind, color, race, hair);
    cache.set(key, c);
  }
  return c;
}
